import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import {
  confirmHubupNotificationPackPayment,
  createHubupNotificationPackCheckout,
  fetchHubupNotificationPackOffers,
  quoteHubupNotificationPack,
} from '../../api/hubup';
import StancerPaymentModal from './StancerPaymentModal';

const STORAGE_KEY = (groupId) => `hubupPackPayment:${groupId}`;

function formatEurTtc(value, { suffix = true } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const formatted = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
  return suffix ? `${formatted} TTC` : formatted;
}

function formatNotifCount(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('fr-FR').format(num);
}

function offerTotalTtc(offer) {
  return offer?.totalPriceTtcEur ?? offer?.totalPriceEur ?? 0;
}

function htDetail(htEur, vatPercent) {
  const ht = Number(htEur);
  if (!Number.isFinite(ht) || ht <= 0) return null;
  const vat = Number(vatPercent) || 20;
  return `${new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(ht)} HT · TVA ${vat} %`;
}

function RecapPriceRow({ label, detail, amountEur }) {
  return (
    <div className="flex justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm text-foreground leading-snug">{label}</p>
        {detail ? (
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{detail}</p>
        ) : null}
      </div>
      <span className="text-sm font-medium text-foreground shrink-0 tabular-nums">
        {formatEurTtc(amountEur, { suffix: false })}
      </span>
    </div>
  );
}

export default function HubupNotificationOffersModal({
  open,
  onClose,
  groupId,
  communityName,
  onPurchaseComplete,
  initialSuccess = false,
  initialError = null,
}) {
  const [step, setStep] = useState('choose');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [offers, setOffers] = useState([]);
  const [meta, setMeta] = useState({
    isPro: true,
    crmUpgradeEur: 0,
    crmUpgradeLabel: '',
    serviceLifetimeLabel: '',
    vatRatePercent: 20,
    affiliateDiscountHtEur: 100,
    affiliateDiscountTtcEur: 120,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [affiliateInput, setAffiliateInput] = useState('');
  const [appliedAffiliateCode, setAppliedAffiliateCode] = useState('');
  const [affiliateApplying, setAffiliateApplying] = useState(false);
  const [affiliateError, setAffiliateError] = useState(null);
  const [quote, setQuote] = useState(null);
  const [stancerPayment, setStancerPayment] = useState(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  const selectedOffer = useMemo(
    () => offers.find((o) => o.id === selectedId) ?? null,
    [offers, selectedId],
  );

  const loadOffers = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchHubupNotificationPackOffers(groupId);
      const data = res?.data ?? {};
      const list = Array.isArray(data.offers) ? data.offers : [];
      setOffers(list);
      setMeta({
        isPro: data.isPro === true,
        crmUpgradeEur: Number(data.crmUpgradeEur) || 0,
        crmUpgradeLabel:
          data.serviceLifetimeLabel ||
          data.crmUpgradeLabel ||
          'Accès au service à vie (paiement unique)',
        serviceLifetimeLabel:
          data.serviceLifetimeLabel ||
          data.crmUpgradeLabel ||
          'Accès au service à vie (paiement unique)',
        vatRatePercent: Number(data.vatRatePercent) || 20,
        affiliateDiscountHtEur: Number(data.affiliateDiscountHtEur) || 100,
        affiliateDiscountTtcEur: Number(data.affiliateDiscountTtcEur) || 120,
      });
      setSelectedId((prev) => prev || list[0]?.id || null);
    } catch (e) {
      setError(e?.message || 'Impossible de charger les offres.');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  const refreshQuote = useCallback(
    async (packId, code) => {
      if (!groupId || !packId) return null;
      const res = await quoteHubupNotificationPack(groupId, {
        packId,
        affiliateCode: code?.trim() || undefined,
      });
      if (res?.success === false) {
        throw new Error(res?.data?.message || 'Total invalide.');
      }
      return res?.data ?? null;
    },
    [groupId],
  );

  useEffect(() => {
    if (!open) return;
    if (initialSuccess) {
      setStep('success');
      setError(null);
      return;
    }
    setStep('choose');
    setAffiliateInput('');
    setAppliedAffiliateCode('');
    setAffiliateError(null);
    setQuote(null);
    setStancerPayment(null);
    setConfirmingPayment(false);
    setError(initialError || null);
    void loadOffers();
  }, [open, loadOffers, initialSuccess, initialError]);

  const goToSummary = async () => {
    if (!selectedId) return;
    setLoading(true);
    setError(null);
    setAffiliateError(null);
    setAffiliateInput('');
    setAppliedAffiliateCode('');
    try {
      const q = await refreshQuote(selectedId, '');
      if (!q) throw new Error('Impossible de calculer le total.');
      setQuote(q);
      setStep('summary');
    } catch (e) {
      setError(e?.message || 'Impossible d’afficher le récapitulatif.');
    } finally {
      setLoading(false);
    }
  };

  const applyAffiliateCode = async (codeOverride) => {
    if (!selectedId) return;
    const code =
      codeOverride !== undefined ? String(codeOverride).trim() : affiliateInput.trim();
    setAffiliateApplying(true);
    setAffiliateError(null);
    try {
      const q = await refreshQuote(selectedId, code || undefined);
      if (!q) throw new Error('Impossible de calculer le total.');
      if (code && !q.affiliateCode) {
        throw new Error('Code promo invalide.');
      }
      setQuote(q);
      setAppliedAffiliateCode(q.affiliateCode || '');
      setAffiliateInput(q.affiliateCode || code);
    } catch (e) {
      setAffiliateError(e?.message || 'Code promo invalide.');
      setAppliedAffiliateCode('');
      try {
        const q = await refreshQuote(selectedId, '');
        setQuote(q);
      } catch {
        /* keep previous quote */
      }
    } finally {
      setAffiliateApplying(false);
    }
  };

  const handleAffiliateInputChange = (value) => {
    setAffiliateInput(value.toUpperCase());
    setAffiliateError(null);
  };

  const handlePay = async () => {
    if (!groupId || !selectedId || loading) return;
    const pendingCode = affiliateInput.trim();
    if (pendingCode && pendingCode !== appliedAffiliateCode) {
      setAffiliateError('Cliquez sur « Appliquer » pour valider le code promo.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      const res = await createHubupNotificationPackCheckout(groupId, {
        packId: selectedId,
        returnUrl,
        affiliateCode: appliedAffiliateCode || undefined,
      });
      const paymentId = res?.data?.paymentId;
      const paymentUrl = res?.data?.paymentUrl;
      if (!paymentId || !paymentUrl) {
        throw new Error('Réponse de paiement invalide.');
      }
      setStancerPayment({ paymentId, paymentUrl });
      sessionStorage.setItem(STORAGE_KEY(groupId), paymentId);
    } catch (e) {
      setError(e?.message || 'Impossible de démarrer le paiement.');
      setStep('summary');
    } finally {
      setLoading(false);
    }
  };

  const handleStancerAuthorized = async (paymentId) => {
    setStancerPayment(null);
    setConfirmingPayment(true);
    setLoading(true);
    setError(null);
    try {
      await confirmHubupNotificationPackPayment(groupId, paymentId);
      sessionStorage.removeItem(STORAGE_KEY(groupId));
      setStep('success');
      onPurchaseComplete?.();
    } catch (e) {
      setError(e?.message || 'Confirmation du paiement impossible.');
      setStep('summary');
    } finally {
      setLoading(false);
      setConfirmingPayment(false);
    }
  };

  const handleStancerError = (message) => {
    setStancerPayment(null);
    setError(message || 'Le paiement a échoué.');
    setStep('summary');
  };

  const handleStancerClose = () => {
    setStancerPayment(null);
  };

  const handleClose = () => {
    if (stancerPayment) {
      setStancerPayment(null);
      return;
    }
    if (confirmingPayment) return;
    onClose?.();
  };

  const baseTotalTtc =
    quote?.totalBeforeTtcEur ??
    (selectedOffer ? offerTotalTtc(selectedOffer) : 0);
  const summaryTotal =
    quote?.totalPriceTtcEur ?? (selectedOffer ? offerTotalTtc(selectedOffer) : 0);
  const affiliateSavings =
    appliedAffiliateCode && baseTotalTtc > summaryTotal
      ? baseTotalTtc - summaryTotal
      : 0;
  const affiliateApplied = Boolean(appliedAffiliateCode);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1200] p-4">
      <div
        className="bg-card rounded-xl shadow-elevation-3 w-full max-w-lg border border-border max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hubup-offers-title"
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-border">
          <div>
            <h2 id="hubup-offers-title" className="text-lg font-semibold text-foreground">
              Recharger vos notifications
            </h2>
            {communityName ? (
              <p className="text-sm text-muted-foreground mt-0.5">{communityName}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
            aria-label="Fermer"
            onClick={handleClose}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error ? (
            <p className="text-sm text-error" role="alert">
              {error}
            </p>
          ) : null}

          {step === 'success' ? (
            <div className="text-center space-y-3 py-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-success/15 text-success">
                <Icon name="Check" size={28} />
              </div>
              <p className="text-sm text-foreground">
                Paiement confirmé. Vos notifications sont disponibles.
              </p>
              <Button type="button" className="w-full" onClick={() => onClose?.()}>
                Continuer
              </Button>
            </div>
          ) : confirmingPayment ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Validation du paiement…
            </p>
          ) : step === 'summary' ? (
            <>
              <p className="text-sm font-medium text-foreground">Récapitulatif de commande</p>

              {selectedOffer ? (
                <div className="rounded-lg border border-border p-4 space-y-3 text-sm">
                  <div className="space-y-3 pb-3 border-b border-border">
                    <RecapPriceRow
                      label={selectedOffer.label}
                      detail={`${formatNotifCount(selectedOffer.notifications)} notifications · ${htDetail(
                        selectedOffer.packPriceHtEur ?? 1000,
                        meta.vatRatePercent,
                      )}`}
                      amountEur={
                        selectedOffer.packPriceTtcEur ??
                        selectedOffer.basePriceTtcEur ??
                        selectedOffer.basePriceEur ??
                        0
                      }
                    />
                    {selectedOffer.includesServiceLifetime ||
                    selectedOffer.includesCrmUpgrade ? (
                      <RecapPriceRow
                        label={
                          meta.serviceLifetimeLabel ||
                          meta.crmUpgradeLabel ||
                          'Accès au service à vie (paiement unique)'
                        }
                        detail={htDetail(
                          selectedOffer.serviceLifetimeHtEur ??
                            meta.crmUpgradeEur ??
                            595,
                          meta.vatRatePercent,
                        )}
                        amountEur={
                          selectedOffer.serviceLifetimeTtcEur ??
                          selectedOffer.crmUpgradeTtcEur ??
                          selectedOffer.crmUpgradeEur ??
                          meta.crmUpgradeEur ??
                          0
                        }
                      />
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground">Sous-total TTC</span>
                      <span
                        className={
                          affiliateApplied && affiliateSavings > 0
                            ? 'text-muted-foreground line-through shrink-0 tabular-nums'
                            : 'font-medium text-foreground shrink-0 tabular-nums'
                        }
                      >
                        {formatEurTtc(baseTotalTtc, { suffix: false })}
                      </span>
                    </div>

                    {affiliateApplied && affiliateSavings > 0 ? (
                      <div className="flex justify-between text-xs text-success">
                        <span>Code promo « {appliedAffiliateCode} »</span>
                        <span className="font-semibold tabular-nums">
                          −{formatEurTtc(affiliateSavings, { suffix: false })}
                        </span>
                      </div>
                    ) : null}

                    <div className="flex justify-between pt-2 border-t border-border">
                      <span className="font-medium text-foreground">Total TTC</span>
                      <span className="font-semibold text-foreground text-lg tabular-nums">
                        {formatEurTtc(summaryTotal, { suffix: false })}
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="rounded-lg border border-border p-4 space-y-2">
                <label
                  htmlFor="hubup-promo-code"
                  className="text-xs font-medium text-foreground block"
                >
                  Code promo
                </label>
                <div className="flex gap-2">
                  <input
                    id="hubup-promo-code"
                    type="text"
                    className={`flex-1 min-w-0 rounded-md border bg-background px-3 py-2 text-sm uppercase font-mono focus:outline-none focus:ring-2 focus:ring-ring ${
                      affiliateError
                        ? 'border-error'
                        : affiliateApplied
                          ? 'border-success'
                          : 'border-input'
                    }`}
                    value={affiliateInput}
                    onChange={(e) => handleAffiliateInputChange(e.target.value)}
                    placeholder="Entrez votre code"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    disabled={affiliateApplying || !affiliateInput.trim()}
                    onClick={() => void applyAffiliateCode()}
                  >
                    {affiliateApplying ? '…' : 'Appliquer'}
                  </Button>
                </div>
                {affiliateApplied ? (
                  <p className="text-xs font-medium text-success">
                    Code promo « {appliedAffiliateCode} » appliqué.
                  </p>
                ) : null}
                {affiliateError ? (
                  <p className="text-xs font-medium text-error" role="alert">
                    {affiliateError}
                  </p>
                ) : null}
                {appliedAffiliateCode ? (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline hover:text-foreground"
                    onClick={() => {
                      setAffiliateInput('');
                      void applyAffiliateCode('');
                    }}
                  >
                    Retirer le code promo
                  </button>
                ) : null}
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  disabled={loading}
                  onClick={() => {
                    setStep('choose');
                    setAffiliateError(null);
                  }}
                >
                  Changer de pack
                </Button>
                <Button
                  type="button"
                  className="flex-1"
                  disabled={loading || confirmingPayment || Boolean(stancerPayment)}
                  onClick={() => void handlePay()}
                >
                  {loading ? 'Préparation…' : 'Payer par carte'}
                </Button>
              </div>
            </>
          ) : loading && offers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Chargement…</p>
          ) : (
            <>
              <div className="space-y-3">
                {offers.map((offer) => {
                  const active = offer.id === selectedId;
                  return (
                    <button
                      key={offer.id}
                      type="button"
                      className={`w-full text-left rounded-lg border p-4 transition-colors ${
                        active
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-border hover:border-primary/40'
                      }`}
                      onClick={() => setSelectedId(offer.id)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-foreground">{offer.label}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatNotifCount(offer.notifications)} notifications
                            {!meta.isPro
                              ? ' · inclut accès au service à vie'
                              : ''}
                          </p>
                        </div>
                        <p className="text-lg font-semibold text-foreground shrink-0">
                          {formatEurTtc(offerTotalTtc(offer))}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={handleClose}>
                  Annuler
                </Button>
                <Button
                  type="button"
                  className="flex-1"
                  disabled={!selectedId || loading}
                  onClick={() => void goToSummary()}
                >
                  {loading ? 'Chargement…' : 'Continuer'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {stancerPayment ? (
        <StancerPaymentModal
          iframeUrl={stancerPayment.paymentUrl}
          paymentId={stancerPayment.paymentId}
          onAuthorized={(id) => void handleStancerAuthorized(id)}
          onError={handleStancerError}
          onClose={handleStancerClose}
        />
      ) : null}
    </div>
  );
}

export async function confirmHubupPackReturnFromUrl(groupId, searchParams) {
  if (searchParams.get('hubupPack') !== 'return' || !groupId) {
    return { ok: false, reason: 'skip' };
  }
  const paymentId = sessionStorage.getItem(STORAGE_KEY(groupId));
  if (!paymentId) {
    return { ok: false, reason: 'no_payment_id' };
  }
  try {
    await confirmHubupNotificationPackPayment(groupId, paymentId);
    sessionStorage.removeItem(STORAGE_KEY(groupId));
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      reason: 'error',
      message: e?.message || 'Confirmation du paiement impossible.',
    };
  }
}
